import { useState, useEffect } from "react";
import "./App.css";

const API_BASE = process.env.REACT_APP_API_BASE || "";

// Available error types for filtering
const ERROR_TYPES = [
  "Disable Account Failed",
  "Modify Account Failed",
  "Create Account Failed",
  "Enable Account Failed",
  "Add Entitlement Failed",
  "Remove Entitlement Failed",
  "Aggregate Source Account Failed",
  "Aggregate Source Entitlement Failed",
  "Change Password Failed"
];

async function apiFetch(path) {
  const res = await fetch(`${API_BASE}${path}`);
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  if (!res.ok) {
    throw new Error(
      data?.error?.localizedMessage ||
        data?.error?.message ||
        data?.message ||
        (typeof data?.error === "string" ? data.error : "") ||
        text ||
        `API error ${res.status}`
    );
  }
  return data;
}

function formatTimeAgo(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function OperationBadge({ operation }) {
  const colors = {
    CREATE: "#3b82f6",
    MODIFY: "#f59e0b",
    DISABLE: "#ef4444",
    ENABLE: "#10b981",
    DELETE: "#dc2626",
  };
  const color = colors[operation] || "#6b7280";

  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "4px",
        fontSize: "11px",
        fontWeight: 600,
        color: "white",
        background: color,
        textTransform: "uppercase",
      }}
    >
      {operation || "UNKNOWN"}
    </span>
  );
}

function SummaryCard({ source, total, byOperation }) {
  return (
    <div className="stat-card">
      <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "10px", color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {source}
      </div>
      <div className="stat-value" style={{ marginBottom: "10px" }}>
        {total}
      </div>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", fontSize: "11px", color: "#6b8ca0" }}>
        {Object.entries(byOperation).map(([op, count]) => (
          <span key={op}>
            {op}: <strong style={{ color: "#a0c5db" }}>{count}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function DetailModal({ error, onClose }) {
  if (!error) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--bg)",
          borderRadius: "8px",
          maxWidth: "800px",
          width: "100%",
          maxHeight: "80vh",
          overflow: "auto",
          padding: "24px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", color: "var(--text-primary)" }}>Provisioning Error Details</h2>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              color: "var(--text-secondary)",
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>TIMESTAMP</div>
            <div style={{ color: "var(--text-primary)" }}>
              {new Date(error.created).toLocaleString()} ({formatTimeAgo(error.created)})
            </div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>SOURCE</div>
            <div style={{ color: "var(--text-primary)" }}>{error.sourceName || "Unknown"}</div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>EMPLOYEE ID</div>
            <div style={{ fontFamily: "monospace", color: "var(--text-primary)" }}>{error.employeeId || "N/A"}</div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>EMPLOYEE EMAIL</div>
            <div style={{ color: "var(--text-primary)" }}>{error.employeeEmail || "Not available"}</div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>OPERATION</div>
            <div>
              <OperationBadge operation={error.operation} />
            </div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ACTION</div>
            <div style={{ color: "var(--text-primary)" }}>{error.action || "N/A"}</div>
          </div>

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ERROR NAME</div>
            <div style={{ color: "var(--text-primary)" }}>{error.name || "N/A"}</div>
          </div>

          {error.accountName && (
            <div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ACCOUNT NAME</div>
              <div style={{ fontFamily: "monospace", color: "var(--text-primary)" }}>{error.accountName}</div>
            </div>
          )}

          {error.accountUuid && (
            <div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ACCOUNT UUID</div>
              <div style={{ fontFamily: "monospace", fontSize: "11px", color: "var(--text-secondary)" }}>
                {error.accountUuid}
              </div>
            </div>
          )}

          {error.trackingNumber && (
            <div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>TRACKING NUMBER</div>
              <div style={{ fontFamily: "monospace", fontSize: "11px", color: "var(--text-secondary)" }}>
                {error.trackingNumber}
              </div>
            </div>
          )}

          {error.attributeName && (
            <div>
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ATTRIBUTE</div>
              <div style={{ color: "var(--text-primary)" }}>
                <strong>{error.attributeName}</strong>: {error.attributeValue || "N/A"}
              </div>
            </div>
          )}

          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "4px" }}>ERROR DESCRIPTION</div>
            <div
              style={{
                background: "var(--bg-raised)",
                border: "1px solid var(--border)",
                borderRadius: "4px",
                padding: "12px",
                fontSize: "14px",
                color: "#ef4444",
                lineHeight: "1.5",
              }}
            >
              {error.error || "No error message"}
            </div>
          </div>

          {error.errorRaw && error.errorRaw !== error.error && (
            <details style={{ marginTop: "12px" }}>
              <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: "12px", marginBottom: "8px" }}>
                Show Technical Details
              </summary>
              <div
                style={{
                  background: "var(--bg-raised)",
                  border: "1px solid var(--border)",
                  borderRadius: "4px",
                  padding: "12px",
                  fontFamily: "monospace",
                  fontSize: "11px",
                  color: "var(--text-secondary)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {error.errorRaw}
              </div>
            </details>
          )}

          <details>
            <summary style={{ cursor: "pointer", color: "var(--text-secondary)", fontSize: "12px" }}>
              Full Event JSON
            </summary>
            <pre
              style={{
                background: "var(--bg-raised)",
                border: "1px solid var(--border)",
                borderRadius: "4px",
                padding: "12px",
                fontSize: "11px",
                overflow: "auto",
                marginTop: "8px",
                color: "var(--text-secondary)",
              }}
            >
              {JSON.stringify(error.fullEvent, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function EmployeeLookupPanel({ onSelect, onClose }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter at least a first or last name.");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSelected(null);
    try {
      const res = await fetch(`${API_BASE}/employee-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="ca-lookup-panel">
      <div className="ca-lookup-header">
        <span className="ca-lookup-title">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          Search by name → populates <strong style={{ color: "var(--text-primary)" }}>Employee ID search</strong>
        </span>
        <button className="ca-lookup-close" onClick={onClose}>✕</button>
      </div>
      <div className="ca-lookup-inputs">
        <div className="ca-lookup-field">
          <label className="control-label">First Name</label>
          <input
            autoFocus
            className="cug-input"
            placeholder="e.g. Jane"
            value={firstName}
            onChange={e => {
              setFirstName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <div className="ca-lookup-field">
          <label className="control-label">Last Name</label>
          <input
            className="cug-input"
            placeholder="e.g. Smith"
            value={lastName}
            onChange={e => {
              setLastName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <button
          className="compare-btn"
          onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}
        >
          {loading ? <><span className="spinner" /> Searching…</> : "Search"}
        </button>
      </div>
      {error && <div className="ca-lookup-error">{error}</div>}
      {results !== null && (
        results.length === 0
          ? <div className="ca-lookup-empty">No employees found. Try different names.</div>
          : (
            <div className="ca-lookup-table-wrap">
              <div className="ca-lookup-thead">
                <div className="ca-lth">EMPLOYEE ID</div>
                <div className="ca-lth">FIRST NAME</div>
                <div className="ca-lth">LAST NAME</div>
                <div className="ca-lth">EMAIL</div>
              </div>
              <div className="ca-lookup-tbody">
                {results.map((r, i) => (
                  <div
                    key={i}
                    className={`ca-lookup-tr ${selected === r.employeeNumber ? "ca-lookup-tr-selected" : ""}`}
                    onClick={() => {
                      setSelected(r.employeeNumber);
                      onSelect(r.employeeNumber);
                    }}
                  >
                    <div className="ca-ltd"><span className="ca-emp-id-badge">{r.employeeNumber || "—"}</span></div>
                    <div className="ca-ltd">{r.firstName || "—"}</div>
                    <div className="ca-ltd">{r.lastName || "—"}</div>
                    <div className="ca-ltd ca-ltd-muted">{r.email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )
      )}
    </div>
  );
}

export default function ProvisioningErrors() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState("7");
  const [sourceFilter, setSourceFilter] = useState("");
  const [errorTypeFilters, setErrorTypeFilters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedError, setSelectedError] = useState(null);
  
  // Employee name search
  const [showEmployeeLookup, setShowEmployeeLookup] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  
  // Error type dropdown
  const [showErrorTypeDropdown, setShowErrorTypeDropdown] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageTokens, setPageTokens] = useState({}); // { pageNumber: searchAfterToken }

  // Close error type dropdown when clicking outside
  useEffect(() => {
    if (!showErrorTypeDropdown) return;
    
    const handleClickOutside = (e) => {
      if (!e.target.closest('.cug-field')) {
        setShowErrorTypeDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showErrorTypeDropdown]);

  async function handleSearch(searchAfterToken = null, page = 1) {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days: days.toString() });
      if (sourceFilter) params.append("source", sourceFilter);
      // Add selected error types to query
      if (errorTypeFilters.length > 0) {
        errorTypeFilters.forEach(et => params.append("errorTypes", et));
      }
      // Use selectedEmployeeId if set, otherwise use searchText
      const searchValue = selectedEmployeeId || searchText;
      if (searchValue) params.append("search", searchValue);
      if (searchAfterToken) params.append("searchAfter", searchAfterToken);

      console.log('Searching with params:', { days, sourceFilter, searchValue, searchAfterToken, page, queryString: params.toString() });
      const result = await apiFetch(`/provisioning-errors?${params}`);
      console.log('Search result:', result);
      setData(result);
      setCurrentPage(page);
      
      // Store the token for the next page
      if (result.nextPageToken) {
        setPageTokens(prev => ({ ...prev, [page + 1]: result.nextPageToken }));
      }
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  
  // Reset to page 1 and clear tokens when starting new search
  const handleNewSearch = () => {
    setPageTokens({});
    setCurrentPage(1);
    handleSearch(null, 1);
  };
  
  // Navigate to next page
  const handleNextPage = () => {
    const nextPage = currentPage + 1;
    const token = pageTokens[nextPage];
    console.log('handleNextPage:', { currentPage, nextPage, token, allTokens: pageTokens });
    if (token) {
      handleSearch(token, nextPage);
    } else {
      console.warn('No token found for next page:', nextPage);
    }
  };
  
  // Navigate to previous page
  const handlePreviousPage = () => {
    const prevPage = currentPage - 1;
    if (prevPage < 1) return;
    const token = pageTokens[prevPage] || null;
    handleSearch(token, prevPage);
  };
  
  // Export current page to CSV
  const handleExport = () => {
    if (!filteredErrors || filteredErrors.length === 0) return;
    
    // CSV headers
    const headers = ["Timestamp", "Source", "Employee ID", "Employee Email", "Error Name", "Error Message"];
    
    // CSV rows
    const rows = filteredErrors.map(err => [
      err.created || "",
      err.sourceName || "",
      err.employeeId || "",
      err.employeeEmail || "",
      err.name || "",
      (err.error || "").replace(/"/g, '""') // Escape double quotes
    ]);
    
    // Build CSV content
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    // Download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    link.setAttribute("href", url);
    link.setAttribute("download", `provisioning-errors-page${currentPage}-${timestamp}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Filter errors based on search
  const filteredErrors =
    data?.errors.filter((err) => {
      // Note: Main search is handled server-side, but we keep client-side filtering
      // for additional refinement on the current page (e.g., filtering by email
      // which is extracted from error messages and not searchable in SailPoint)

      return true;
    }) || [];

  return (
    <div className="cug-root">
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark">V</div>
          <div>
            <div className="cug-title">Provisioning Errors</div>
            <div className="cug-subtitle">Monitor failed provisioning events across IdentityNow applications</div>
          </div>
        </div>
      </div>

      <div style={{ padding: "0 32px", marginBottom: "24px" }}>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
          <div className="cug-field" style={{ minWidth: "140px" }}>
            <label className="control-label" style={{ display: "block", marginBottom: "6px" }}>Days</label>
            <select value={days} onChange={(e) => setDays(e.target.value)} className="form-control">
              <option value="1">Last 1 day</option>
              <option value="7">Last 7 days</option>
              <option value="15">Last 15 days</option>
              <option value="30">Last 30 days</option>
              <option value="ytd">Year to Date</option>
            </select>
          </div>

          <div className="cug-field" style={{ minWidth: "250px" }}>
            <label className="control-label" style={{ display: "block", marginBottom: "6px" }}>Source (optional)</label>
            <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="form-control">
              <option value="">All sources</option>
              <option value="MYHR INFORLANDMARK PROD">MYHR INFORLANDMARK PROD</option>
              <option value="MYHR TABLEAU PROD">MYHR TABLEAU PROD</option>
              <option value="MYHR TALENT SOLUTIONS 2.0 - AVATURE(PRODUCTION)">MYHR TALENT SOLUTIONS 2.0 - AVATURE(PRODUCTION)</option>
              <option value="MYHR INFOR/HS PROD">MYHR INFOR/HS PROD</option>
              <option value="MYHR VISIER PROD">MYHR VISIER PROD</option>
            </select>
          </div>

          <div className="cug-field" style={{ minWidth: "300px", position: "relative" }}>
            <label className="control-label" style={{ display: "block", marginBottom: "6px" }}>Error Types (optional)</label>
            <div 
              className="form-control"
              onClick={() => setShowErrorTypeDropdown(!showErrorTypeDropdown)}
              style={{ 
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                userSelect: "none"
              }}
            >
              <span style={{ fontSize: "13px" }}>
                {errorTypeFilters.length === 0 
                  ? "All Error Types" 
                  : errorTypeFilters.length === ERROR_TYPES.length
                  ? "All Error Types"
                  : `${errorTypeFilters.length} selected`}
              </span>
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ transform: showErrorTypeDropdown ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </div>
            {showErrorTypeDropdown && (
              <div style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: "4px",
                border: "1px solid var(--border)",
                borderRadius: "4px",
                background: "var(--bg-secondary)",
                padding: "8px",
                maxHeight: "250px",
                overflowY: "auto",
                zIndex: 1000,
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.3)"
              }}>
                <div style={{ marginBottom: "6px", paddingBottom: "6px", borderBottom: "1px solid var(--border)" }}>
                  <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "12px", color: "var(--text-primary)" }}>
                    <input
                      type="checkbox"
                      checked={errorTypeFilters.length === ERROR_TYPES.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setErrorTypeFilters([...ERROR_TYPES]);
                        } else {
                          setErrorTypeFilters([]);
                        }
                      }}
                      style={{ marginRight: "6px" }}
                    />
                    <strong>Select All</strong>
                  </label>
                </div>
                {ERROR_TYPES.map((errorType) => (
                  <div key={errorType} style={{ marginBottom: "4px" }}>
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", fontSize: "12px", color: "var(--text-primary)" }}>
                      <input
                        type="checkbox"
                        checked={errorTypeFilters.includes(errorType)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setErrorTypeFilters([...errorTypeFilters, errorType]);
                          } else {
                            setErrorTypeFilters(errorTypeFilters.filter(t => t !== errorType));
                          }
                        }}
                        style={{ marginRight: "6px" }}
                      />
                      {errorType}
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={handleNewSearch} disabled={loading} title="Search errors" className="compare-btn" style={{ marginBottom: "2px" }}>
            {loading ? (
              <>
                <span className="spinner" />
                Searching...
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <path d="m21 21-4.35-4.35"/>
                </svg>
                Search
              </>
            )}
          </button>

          <button
            className={`as-lookup-toggle ${showEmployeeLookup ? "as-lookup-toggle-active" : ""}`}
            onClick={() => setShowEmployeeLookup(v => !v)}
            style={{ marginBottom: "2px" }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            {showEmployeeLookup ? "Hide Name Search" : "Don't know the Employee ID?"}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
              style={{ transform: showEmployeeLookup ? "rotate(180deg)" : "none", transition: "transform 0.2s", marginLeft: "4px" }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>
        </div>

        {selectedEmployeeId && (
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "8px",
            padding: "8px 12px", 
            background: "rgba(112, 204, 152, 0.1)", 
            border: "1px solid #70cc98", 
            borderRadius: "4px",
            fontSize: "13px",
            color: "#70cc98",
            fontWeight: 500
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
            </svg>
            Searching for Employee ID: {selectedEmployeeId}
            <button
              onClick={() => setSelectedEmployeeId("")}
              style={{
                background: "transparent",
                border: "none",
                color: "#70cc98",
                cursor: "pointer",
                padding: "2px 4px",
                display: "flex",
                alignItems: "center",
                marginLeft: "auto"
              }}
              title="Clear employee filter"
            >
              ✕
            </button>
          </div>
        )}

        {showEmployeeLookup && (
          <div className="as-lookup-wrap" style={{ marginTop: "16px" }}>
            <EmployeeLookupPanel
              onSelect={(empId) => {
                setSelectedEmployeeId(empId);
                setSearchText("");
                setShowEmployeeLookup(false);
              }}
              onClose={() => setShowEmployeeLookup(false)}
            />
          </div>
        )}
      </div>

      <div className="cug-body sn-cug-body">
        {/* Error state */}
        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
            <button className="error-banner-close" onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Initial state - no search yet */}
        {!loading && !error && !data && (
          <div className="cug-empty-state">
            <div style={{ fontSize: "16px", color: "#a0c5db", marginBottom: "8px" }}>👆 Choose your filters and click Search</div>
            <div style={{ fontSize: "13px", color: "#6b8ca0" }}>Select a time period to view provisioning errors</div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="cug-empty-state">
            <span className="spinner" />
            <div style={{ marginTop: "12px", color: "#6b8ca0" }}>Loading provisioning errors...</div>
          </div>
        )}

        {/* Summary cards */}
        {!loading && data?.summary && data.summary.length > 0 && (
          <div style={{ marginBottom: "28px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 600, color: "#8ba2b8", marginBottom: "14px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Summary by Source
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px" }}>
              {data.summary.map((item) => (
                <SummaryCard
                  key={item.sourceName}
                  source={item.sourceName}
                  total={item.total}
                  byOperation={item.byOperation}
                />
              ))}
            </div>
          </div>
        )}

        {/* Errors table */}
        {!loading && !error && (
          <>
            {/* Top Controls: Pagination + Export */}
            {data && filteredErrors.length > 0 && (
              <div style={{ 
                marginBottom: "16px", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.02)",
                border: "1px solid var(--border)",
                borderRadius: "6px"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button 
                    onClick={handlePreviousPage} 
                    disabled={currentPage === 1 || loading}
                    className="compare-btn"
                    style={{ 
                      opacity: currentPage === 1 ? 0.4 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    ← Previous
                  </button>
                  
                  <div style={{ 
                    fontSize: "14px", 
                    color: "var(--text-primary)", 
                    fontWeight: 500 
                  }}>
                    Page {currentPage}
                  </div>
                  
                  <button 
                    onClick={handleNextPage} 
                    disabled={!data.hasMore || loading}
                    className="compare-btn"
                    style={{ 
                      opacity: !data.hasMore ? 0.4 : 1,
                      cursor: !data.hasMore ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Next →
                  </button>
                </div>
                
                <input
                  type="text"
                  placeholder={selectedEmployeeId ? `Searching for Employee ID: ${selectedEmployeeId}` : "Search anything in the table..."}
                  value={selectedEmployeeId ? "" : searchText}
                  onChange={(e) => !selectedEmployeeId && setSearchText(e.target.value)}
                  disabled={!!selectedEmployeeId}
                  style={{
                    flex: 1,
                    maxWidth: "400px",
                    margin: "0 16px",
                    padding: "8px 12px",
                    background: selectedEmployeeId ? "rgba(112, 204, 152, 0.1)" : "rgba(255,255,255,0.05)",
                    border: selectedEmployeeId ? "1px solid #70cc98" : "1px solid var(--border)",
                    borderRadius: "4px",
                    color: selectedEmployeeId ? "#70cc98" : "var(--text-primary)",
                    fontSize: "13px",
                    outline: "none",
                    cursor: selectedEmployeeId ? "not-allowed" : "text"
                  }}
                  onFocus={(e) => !selectedEmployeeId && (e.target.style.borderColor = "#70cc98")}
                  onBlur={(e) => !selectedEmployeeId && (e.target.style.borderColor = "var(--border)")}
                />
                
                <button 
                  onClick={handleExport}
                  disabled={loading}
                  className="compare-btn"
                  title="Export current page to CSV"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "6px" }}>
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
              </div>
            )}
            
            <div style={{ marginBottom: "14px", fontSize: "13px", color: "#6b8ca0", fontWeight: 500 }}>
              Showing {filteredErrors.length} errors on Page {currentPage}
              {data?.hasMore && (
                <span style={{ marginLeft: "12px", color: "#70cc98", fontSize: "12px" }}>
                  • More pages available (use Next button to load more)
                </span>
              )}
            </div>

            {filteredErrors.length === 0 ? (
              <div className="cug-empty-state">
                No errors found
              </div>
            ) : (
              <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid var(--border)", borderRadius: "6px", overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)", borderBottom: "1px solid var(--border)" }}>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em", width: "100px" }}>TIMESTAMP</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em", width: "180px" }}>SOURCE</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em", width: "220px" }}>EMPLOYEE ID / EMAIL</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em", width: "180px" }}>ERROR NAME</th>
                      <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>ERROR</th>
                      <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em", width: "100px" }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredErrors.map((err) => (
                      <tr 
                        key={err.id}
                        style={{ cursor: "pointer", borderBottom: "1px solid var(--border)", transition: "background 0.2s" }}
                        onClick={() => setSelectedError(err)}
                        onMouseEnter={(e) => e.currentTarget.style.background = "rgba(112, 204, 152, 0.05)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                      >
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#a0c5db" }}>{formatTimeAgo(err.created)}</td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#d4e3ed" }}>{err.sourceName || "Unknown"}</td>
                        <td style={{ padding: "14px 16px", fontSize: "13px" }}>
                          <div style={{ fontFamily: "monospace", color: "#70cc98", marginBottom: "2px" }}>{err.employeeId || "N/A"}</div>
                          <div style={{ fontSize: "11px", color: "#8ba2b8" }}>{err.employeeEmail || "No email"}</div>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#a0c5db" }}>{err.name || "N/A"}</td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#d4e3ed", maxWidth: "500px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {err.error || "No error message"}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center" }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedError(err);
                            }}
                            style={{
                              background: "transparent",
                              border: "1px solid var(--border)",
                              borderRadius: "4px",
                              padding: "4px 12px",
                              fontSize: "12px",
                              color: "#70cc98",
                              cursor: "pointer",
                              transition: "all 0.2s"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "rgba(112, 204, 152, 0.1)";
                              e.currentTarget.style.borderColor = "#70cc98";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.borderColor = "var(--border)";
                            }}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination Controls */}
            {!loading && !error && data && filteredErrors.length > 0 && (
              <div style={{ 
                marginTop: "20px", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.02)",
                border: "1px solid var(--border)",
                borderRadius: "6px"
              }}>
                <button 
                  onClick={handlePreviousPage} 
                  disabled={currentPage === 1 || loading}
                  className="compare-btn"
                  style={{ 
                    opacity: currentPage === 1 ? 0.4 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ← Previous
                </button>
                
                <div style={{ 
                  fontSize: "14px", 
                  color: "var(--text-primary)", 
                  fontWeight: 500 
                }}>
                  Page {currentPage}
                  {data.hasMore && (
                    <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> • More results available</span>
                  )}
                </div>
                
                <button 
                  onClick={handleNextPage} 
                  disabled={!data.hasMore || loading}
                  className="compare-btn"
                  style={{ 
                    opacity: !data.hasMore ? 0.4 : 1,
                    cursor: !data.hasMore ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {/* Detail modal */}
        {selectedError && <DetailModal error={selectedError} onClose={() => setSelectedError(null)} />}
      </div>
    </div>
  );
}
